import type { Perspective } from './perspective';
import { discardCandidates } from './resolve/turn';
import type { GameState } from './types/state';
import type { MyView, PlayerPublicView, PlayerView, ViewDecision } from './types/view';

export const VIEW_LOG_LIMIT = 80;

/**
 * Construye la vista de un jugador por LISTA BLANCA: nunca se copia el estado completo.
 * Oculta manos y selecciones ajenas, mazos, contenido del descarte y la RNG.
 */
export function toPlayerView(state: GameState, viewerSeat: number | null): PlayerView {
  const d = state.decision;
  let decision: ViewDecision;
  if (d.kind === 'special') {
    const head = d.queue[0]!;
    decision = { kind: 'special', seat: head.seat, type: head.type };
  } else {
    decision = { ...d };
  }

  const players: PlayerPublicView[] = state.players.map((p) => ({
    seat: p.seat,
    userId: p.userId,
    name: p.name,
    wonder: p.wonder,
    side: p.side,
    stagesBuilt: p.stagesBuilt,
    city: [...p.city],
    coins: p.coins,
    status: p.status,
    handCount: p.hand.length,
    military: p.military.map((t) => ({ ...t })),
    ready: d.kind === 'sideSelection' ? p.sideChoice !== null : d.kind === 'turn' ? p.selection !== null : false,
    ...(p.frozen ? { frozen: { ...p.frozen } } : {}),
  }));

  let me: MyView | undefined;
  const mine = viewerSeat === null ? undefined : state.players[viewerSeat];
  if (mine) {
    me = {
      seat: mine.seat,
      hand: [...mine.hand],
      selection: mine.selection ? JSON.parse(JSON.stringify(mine.selection)) : null,
      sideChoice: mine.sideChoice,
    };
    if (d.kind === 'special') {
      const head = d.queue[0]!;
      if (head.seat === mine.seat) {
        me.special =
          head.type === 'buildFromDiscard'
            ? { type: 'buildFromDiscard', candidates: discardCandidates(state, mine.seat) }
            : { type: 'playLastCard', card: head.card };
      }
    }
  }

  return {
    engineVersion: state.engineVersion,
    viewerSeat,
    age: state.age,
    turn: state.turn,
    decisionId: state.decisionId,
    decision,
    players,
    discardCount: state.discard.length,
    ...(me ? { me } : {}),
    log: state.log.slice(-VIEW_LOG_LIMIT).map((e) => JSON.parse(JSON.stringify(e))),
    result: state.result ? JSON.parse(JSON.stringify(state.result)) : null,
  };
}

/** Perspectiva del jugador a partir de su vista (cliente): mismo cálculo de jugadas que el servidor. */
export function perspectiveFromView(view: PlayerView): Perspective | null {
  if (!view.me) return null;
  const special = view.me.special;
  const lastCard = special?.type === 'playLastCard';
  return {
    seat: view.me.seat,
    age: view.age,
    turn: view.turn,
    cities: view.players.map((p) => ({
      seat: p.seat,
      wonder: p.wonder,
      side: p.side,
      stagesBuilt: p.stagesBuilt,
      city: p.city,
      coins: p.coins,
      status: p.status,
    })),
    hand: lastCard ? [special.card] : view.me.hand,
    mode: lastCard ? 'playLastCard' : 'turn',
  };
}
